"use client";

import {
  CalendarDotsIcon,
  CheckIcon,
  CrownSimpleIcon,
  RocketLaunchIcon,
} from "@phosphor-icons/react";
import { formatPlanRenovationDate } from "@/components/features/center-stage/shared/_utils/date-formatters";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Typography } from "@/components/ui/typography";
import { useLocalizedRouter } from "@/hooks/use-localized-router";
import { useTranslation } from "@/i18n/client";
import { type Plan, PlanType } from "@/repositories/plan/types";

export default function SidebarPlanMenu({ plan, isMobile = false }: { plan: Plan | null; isMobile?: boolean }) {
  const { t: tSidebar } = useTranslation("sidebar");
  const { t: tPlan } = useTranslation("cv-download");
  const { navigateTo } = useLocalizedRouter();

  const formattedDate = formatPlanRenovationDate(plan?.renovationDate || "");

  const handleManageSubscription = () => {
    if (plan?.status !== "ACTIVE") {
      navigateTo("/plans");
      return;
    }
    navigateTo("/settings/billing");
  };

  if (plan?.planType === PlanType.FREE || !plan) {
    if (isMobile) {
      return (
        <button
          type="button"
          className="w-full rounded-xl px-3 py-2 text-left cursor-pointer"
          style={{
            background: "linear-gradient(90deg, #FFBB57 0%, #EC68A3 50%, #A092E1 100%)",
          }}
          onClick={handleManageSubscription}
        >
          <Typography
            variant="label 4"
            className="flex items-center text-neutral-0 font-semibold"
          >
            <CrownSimpleIcon weight="fill" className="text-neutral-0 mr-2" />
            {tSidebar("enhanceProfile")}
          </Typography>
        </button>
      );
    }

    return (
      <button
        type="button"
        className="w-[216px] h-[199px] rounded-xl p-[1px] text-left cursor-pointer"
        style={{
          background: "radial-gradient(138% 100% at 50% 0%, #FFBB57 0%, #EC68A3 22%, #A092E1 100%)",
        }}
        onClick={handleManageSubscription}
      >
        <Card className="w-full h-full bg-[radial-gradient(158.42%_100%_at_50%_0%,#FFBB57_0%,#EC68A3_25%,#816DD7_65%,#F6F5FF_87.02%)] rounded-[11px] shadow-none border-0 flex flex-col">
          <CardContent className="p-0 pt-5 px-4 pb-3 flex-1">
            <CrownSimpleIcon weight="fill" className="text-neutral-0 w-5 h-5 mb-2" />

            <Typography variant="heading 4" className="text-neutral-0 font-bold text-lg leading-6">
              {tSidebar("enhanceProfile")}
            </Typography>

            <div className="mt-2 flex flex-col gap-1">
              <Typography variant="label 5" className="flex items-center text-neutral-0 gap-1">
                <CheckIcon />
                {tSidebar("stand-out")}
              </Typography>
              <Typography variant="label 5" className="flex items-center text-neutral-0 gap-1">
                <CheckIcon />
                {tSidebar("improve-cv-ai")}
              </Typography>
              <Typography variant="label 5" className="flex items-center text-neutral-0 gap-1">
                <CheckIcon />
                {tSidebar("custom-positions")}
              </Typography>
            </div>
          </CardContent>

          <CardFooter className="flex justify-center items-center gap-2 p-0 py-3 px-2">
            <RocketLaunchIcon weight="fill" size={18} className="text-primary-900" />
            <Typography className="text-primary-900 text-xs font-semibold">
              {tSidebar("know-more")}
            </Typography>
          </CardFooter>
        </Card>
      </button>
    );
  }

  if (isMobile) {
    return (
      <button
        type="button"
        onClick={handleManageSubscription}
        className="w-full text-left cursor-pointer"
      >
        <Card className="border border-[#FFBB57] px-3 py-2">
          <Typography
            variant="label 4"
            className="flex items-center text-primary-600 font-semibold"
          >
            <CrownSimpleIcon weight="fill" className="text-yellow-500 mr-2" />
            {tPlan(`plan-types.${plan.planType}`)}
          </Typography>
        </Card>
      </button>
    );
  }

  return (
    <Card className="flex flex-col w-[216px] border border-[#FFBB57] p-3 gap-3">
      <CardHeader className="p-0">
        <Typography
          variant="label 4"
          className="flex items-center text-primary-600 font-semibold mb-2"
        >
          <CrownSimpleIcon weight="fill" className="text-yellow-500 mr-2" />
          {tPlan(`plan-types.${plan.planType}`)}
        </Typography>
        <Button variant="secondary" className="w-full h-8">
          <CalendarDotsIcon className="text-primary-500" />
          <Typography variant="label 6" className="text-neutral-600">
            {plan.cancelAtPeriodEnd
              ? tSidebar("expire-date-prefix")
              : tSidebar("renewal-date-prefix")
            } <strong>{formattedDate}</strong>
          </Typography>
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full">
          <thead className="border-b border-neutral-200">
            <tr>
              <th className="text-start">
                <Typography variant="label 6" className="text-neutral-600">
                  {tSidebar("benefit")}
                </Typography>
              </th>
              <th className="text-end">
                <Typography variant="label 6" className="text-neutral-600">
                  {tSidebar("credits")}
                </Typography>
              </th>
            </tr>
          </thead>
          <tbody>
            {plan?.features.map((feature) => (
              <tr key={feature.feature}>
                <td>
                  <Typography variant="label 6" className="text-neutral-700">
                    {tSidebar(`features.${feature.feature}`)}
                  </Typography>
                </td>
                <td className="text-right">
                  <Typography variant="label 6" className="text-primary-600">
                    {feature.availableCredits}
                  </Typography>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
      <CardFooter className="p-0">
        <Button
          variant="primary"
          className="w-full h-8 bg-primary-100 font-semibold text-primary-600 hover:bg-primary-100"
          onClick={handleManageSubscription}
        >
          {tSidebar("manage-subscription")}
        </Button>
      </CardFooter>
    </Card>
  );
}
